import {
  Box,
  Grid,
  Skeleton,
  Text,
  Button,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContext } from "react";
import PropertyCard from "../PropertyCard/PropertyCard";
import {
  PropertiesContext,
  PropertiesContextType,
} from "../../../../contexts/PropertiesContext";

export default function PropertyList() {
  const {
    properties,
    isLoading,
    error,
    handlePropertyClick,
    filters,
    setFilters,
    count,
  } = useContext<PropertiesContextType | undefined>(
    PropertiesContext
  ) as PropertiesContextType;

  if (error) {
    return (
      <Box p={4} color="red.500">
        Erro ao carregar propriedades: {error.message}
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Grid
        templateColumns="repeat(auto-fit, minmax(280px, 1fr))"
        gap={6}
        maxW="100%"
      >
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} height="300px" borderRadius="md" />
        ))}
      </Grid>
    );
  }

  if (!properties.length) {
    return (
      <Box p={8} textAlign="center" color="gray.500">
        <Text fontSize="lg">Nenhuma propriedade encontrada</Text>
        <Text fontSize="sm" mt={2}>
          Tente ajustar os filtros de busca
        </Text>
      </Box>
    );
  }

  // Calculate pagination
  const currentPage = filters.page || 1;
  const itemsPerPage = filters.limit || 12;
  const totalPages = Math.ceil(count / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, count);

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  return (
    <Box>
      {/* Properties Grid - 4 columns on desktop, responsive on smaller screens */}
      <Grid
        templateColumns={{
          base: "1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(3, 1fr)",
          lg: "repeat(4, 1fr)",
        }}
        gap={6}
        mb={8}
      >
        {properties.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            onClick={() => handlePropertyClick(property.id)}
          />
        ))}
      </Grid>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={4}
          borderTop="1px"
          borderColor="gray.200"
        >
          {/* Results Info */}
          <Text fontSize="sm" color="gray.600">
            Exibindo {startItem}-{endItem} de {count} propriedades
          </Text>

          {/* Pagination Buttons */}
          <HStack spacing={2}>
            <IconButton
              aria-label="Página anterior"
              icon={<ChevronLeft size={16} />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === 1}
              onClick={handlePrevPage}
            />

            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  size="sm"
                  variant={currentPage === pageNum ? "solid" : "outline"}
                  colorScheme={currentPage === pageNum ? "blue" : "gray"}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}

            <IconButton
              aria-label="Próxima página"
              icon={<ChevronRight size={16} />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === totalPages}
              onClick={handleNextPage}
            />
          </HStack>
        </Box>
      )}
    </Box>
  );
}
